import idx from "idx";
import { PreferredLocale } from "../../../../../utils/locales";
import { EMPTY_CONTENT } from "../../../../course-author/html-editor/Editor.helpers";
import {
  CourseComponentType,
  IChoiceExerciseItem,
  IClozeExercise,
  IClozeGap,
  ICourseInstructionContentPart,
  IDividerPart,
  IDocumentPart,
  IGridPart,
  IImagePart,
  IListItem,
  IListWithOptionsExercise,
  IListWithoutOptionsExercise,
  ILocalizedListItem,
  IMultipleChoiceExercise,
  ISingleChoiceExercise,
  ISpacerPart,
  ITeachingContent,
  IUnorderedListExercise,
} from "../../../../course-author/types";
import { ICourseComponentPart } from "../../../../../App/Analytics/Common";

export function isTeachingPart(data: ICourseInstructionContentPart | ICourseComponentPart): data is ITeachingContent {
  return idx(data, d => d.tag) === CourseComponentType.Teaching;
}

export function isMultipleChoicePart(
  data: ICourseInstructionContentPart | ICourseComponentPart
): data is IMultipleChoiceExercise {
  if (data.tag === CourseComponentType.Choice) {
    if ("singleChoice" in data.content) {
      return !!data.content.singleChoice === false;
    }
  }

  return false;
}

export function isSingleChoicePart(
  data: ICourseInstructionContentPart | ICourseComponentPart
): data is ISingleChoiceExercise {
  if (data.tag === CourseComponentType.Choice) {
    if ("singleChoice" in data.content) {
      return !!data.content.singleChoice === true;
    }
  }

  return false;
}

export function isClozePart(data: ICourseInstructionContentPart | ICourseComponentPart): data is IClozeExercise {
  return idx(data, d => d.tag) === CourseComponentType.Cloze;
}

export function isUnorderedListPart(
  data: ICourseInstructionContentPart | ICourseComponentPart
): data is IUnorderedListExercise {
  return idx(data, d => d.tag) === CourseComponentType.UnorderedList;
}

export const isListWithoutOptionsPart = (
  data: ICourseInstructionContentPart | ICourseComponentPart
): data is IListWithoutOptionsExercise => idx(data, d => d.tag) === CourseComponentType.ListWithoutOptions;

export const isListWithOptionsPart = (
  data: ICourseInstructionContentPart | ICourseComponentPart
): data is IListWithOptionsExercise => idx(data, d => d.tag) === CourseComponentType.ListWithOptions;

export const isGridPart = (data: ICourseInstructionContentPart | ICourseComponentPart): data is IGridPart =>
  data.tag === CourseComponentType.Grid;

export const isDividerPart = (data: ICourseInstructionContentPart | ICourseComponentPart): data is IDividerPart =>
  data.tag === CourseComponentType.Divider;

export const isSpacerPart = (data: ICourseInstructionContentPart | ICourseComponentPart): data is ISpacerPart =>
  data.tag === CourseComponentType.Spacer;

export const isImagePart = (data: ICourseInstructionContentPart | ICourseComponentPart): data is IImagePart =>
  data.tag === CourseComponentType.Image;

export const isDocumentPart = (data: ICourseInstructionContentPart | ICourseComponentPart): data is IDocumentPart =>
  data.tag === CourseComponentType.Document;

export const gridColumnName = (data: IGridPart): string => {
  switch (data.content.columns.tag) {
    case "TwoColumns":
      return "two column";
    case "ThreeColumns":
      return "three column";
    default:
      return "unknown columns";
  }
};

export const gridLayoutName = (data: IGridPart) => data.content.layout.content.join(",");

export function malformedWarnings(contentPart: ICourseInstructionContentPart, locale: PreferredLocale): string[] {
  if (isMultipleChoicePart(contentPart)) {
    const exercises: IChoiceExerciseItem[] = contentPart.content.items.elems.filter(elem => !elem.deleted);

    if (!exercises.some(item => item.correct)) {
      return ["This question must have at least one correct answer."];
    }

    if (exercises.some(item => isCorrectButEmpty(item, locale))) {
      return ["Selected answer(s) doesn't have content for display"];
    }
  }

  if (isSingleChoicePart(contentPart)) {
    const exercises: IChoiceExerciseItem[] = contentPart.content.items.elems.filter(elem => !elem.deleted);

    if (!exercises.some(item => item.correct)) {
      return ["There is no correct answer for this question."];
    }

    if (exercises.some(item => isCorrectButEmpty(item, locale))) {
      return ["Selected answer doesn't have content for display"];
    }
  }

  if (isClozePart(contentPart)) {
    const exercises: IClozeGap[] = contentPart.content.gaps.elems.filter(elem => !elem.deleted);

    if (exercises.length === 0) {
      return ["There are no gaps to fill in."];
    }
  }

  if (isUnorderedListPart(contentPart) || isListWithoutOptionsPart(contentPart)) {
    const exercises: ILocalizedListItem[] = contentPart.content.items.elems.filter(elem => !elem.deleted);

    if (exercises.length === 0) {
      return ["There are no items to list."];
    }

    if (exercises.some(e => e.answers.elems.length === 0)) {
      return ["Some of the items have no answers."];
    }
  }

  if (isListWithOptionsPart(contentPart)) {
    const exercises: IListItem[] = contentPart.content.items.elems.filter(elem => !elem.deleted);

    if (exercises.length === 0) {
      return ["There are no items to order in this question."];
    }
  }

  return [];
}

function isCorrectButEmpty(item: IChoiceExerciseItem, locale: PreferredLocale): boolean {
  const answer = item.text.elems.find(elem => elem.locale === locale);
  return item.correct && (item.text.elems.length === 0 || (!!answer && answer.value.text === EMPTY_CONTENT));
}
